import { useId } from 'react'

type PublicSwitchProps = {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  description?: string
  disabled?: boolean
  busy?: boolean
  /** Nombre accesible completo cuando la etiqueta visible es corta o repetida (p. ej. en filas de tabla). */
  ariaLabel?: string
  compact?: boolean
}

/**
 * Interruptor accesible (role="switch") con objetivo táctil de 44px en
 * punteros gruesos. Controlado: el estado optimista vive en la caché de
 * react-query, no aquí.
 */
export function PublicSwitch({ checked, onChange, label, description, disabled, busy, ariaLabel, compact }: PublicSwitchProps) {
  const descId = useId()
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel ?? label}
        aria-describedby={description ? descId : undefined}
        aria-busy={busy || undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className="group relative flex shrink-0 items-center justify-center [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span
          className={`block h-6 w-11 rounded-full transition-colors duration-200 group-focus-visible:ring-2 group-focus-visible:ring-brand-500 group-focus-visible:ring-offset-2 ${
            checked ? 'bg-brand-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        />
        <span
          className={`absolute left-1/2 top-1/2 -mt-2.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
            checked ? '-ml-[22px] translate-x-[22px]' : '-ml-[22px] translate-x-0.5'
          }`}
        />
      </button>
      <div className="min-w-0 self-center">
        {!compact && <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>}
        {description && (
          <p id={descId} className={`text-xs text-gray-500 dark:text-gray-400 ${compact ? '' : 'mt-0.5'}`}>
            {description}
          </p>
        )}
      </div>
    </div>
  )
}
